// The Customers tab: one list with segments (who needs attention and why, who was
// lost, who is new or back, everyone), a panel for the chosen customer, and a call sheet.
import { useEffect, useMemo, useState } from "react";
import { getCustomerHealth } from "../../distflowAPI/salesAnalyticsApi";
import CallSheet from "./CallSheet";
import SplitView from "./SplitView";
import { cadence, daysAgo, plural, STATUS_TONES } from "./customerText";
import { customerLevelFilters, hasItemLevelFilter } from "./drillDown";
import { count, money, pct, shortDate, shortRange, signedMoney, signedPct, tone } from "./format";
import useSideReport from "./useSideReport";
import "./CustomerList.css";

export const CUSTOMER_LISTS = [
  { key: "attention", label: "Needs attention", source: "health" },
  { key: "late", label: "Late", source: "health", statuses: ["late"] },
  { key: "switching", label: "Buying elsewhere?", source: "health", statuses: ["switching"] },
  { key: "less", label: "Spending less", source: "health", statuses: ["spending_less", "ordering_less", "smaller_orders"] },
  { key: "lost", label: "Lost", source: "lost", comparison: true },
  { key: "new", label: "New", source: "new" },
  { key: "back", label: "Back again", source: "reactivated", comparison: true },
  { key: "all", label: "All customers", source: "all" },
];
const PAGE = 25;
const EMPTY = [];

function FollowUp({ followUp }) {
  if (followUp?.open_tasks) {
    return <span className="sa-follow sa-follow--open">{followUp.open_tasks} open {plural(followUp.open_tasks, "task")}</span>;
  }
  if (followUp?.last_task) {
    return <span className="sa-follow">{followUp.last_task.done ? "Done" : "Task"} {shortDate(followUp.last_task.date)}</span>;
  }
  return <span className="sa-follow sa-follow--none">No task</span>;
}

function Name({ row, sub, openKey, onOpen }) {
  return <th scope="row" className="sa-col-name">
    <button type="button" className="sa-row-link" aria-current={openKey === row.key ? "true" : undefined}
      aria-controls="sa-detail-panel" onClick={(event) => { event.stopPropagation(); onOpen(row.key); }}>{row.name || "Unnamed customer"}</button>
    {sub && <small>{sub}</small>}
  </th>;
}

function columnsFor(source, { hasComparison, comparisonDates }) {
  if (source === "health") return ["Customer", "Why", "At risk / wk", "Last order", "Follow-up"];
  if (source === "lost") return ["Customer", `Bought ${comparisonDates}`, "Orders then", "Last order"];
  if (source === "new") return ["Customer", "Sales", "Orders", "First order"];
  if (source === "reactivated") return ["Customer", "Sales", "Orders", "Customer since"];
  return ["Customer", "Sales", ...(hasComparison ? [comparisonDates, "Change"] : []), "Share"];
}

function Cells({ source, row, hasComparison, labels }) {
  const label = (index) => ({ "data-label": labels[index + 1] });
  if (source === "health") {
    const main = row.reasons?.find((reason) => reason.type === row.status) || row.reasons?.[0];
    return <>
      <td className="sa-cust-why sa-cell-wide sa-cell-plain" {...label(0)}><span className={`sa-badge sa-badge--${STATUS_TONES[row.status] || "quiet"}`}>{row.status_label}</span><small>{main?.detail}</small></td>
      <td className="sa-num sa-cust-risk" {...label(1)}>{money(row.at_risk_weekly)}</td>
      <td className="sa-cust-when" {...label(2)}>{shortDate(row.last_order_date)}<small>{daysAgo(row.days_since_last_order)}</small></td>
      <td {...label(3)}><FollowUp followUp={row.follow_up} /></td>
    </>;
  }
  if (source === "lost") {
    return <>
      <td className="sa-num" {...label(0)}>{money(row.previous_sales)}</td>
      <td className="sa-num" {...label(1)}>{count(row.previous_orders)}</td>
      <td className="sa-cust-when" {...label(2)}>{shortDate(row.last_order_date, { year: true })}<small>{daysAgo(row.days_since_last_order)}</small></td>
    </>;
  }
  if (source === "new" || source === "reactivated") {
    return <>
      <td className="sa-num" {...label(0)}>{money(row.sales)}</td>
      <td className="sa-num" {...label(1)}>{count(row.orders)}</td>
      <td className="sa-cust-when" {...label(2)}>{shortDate(row.first_order_date, { year: true })}</td>
    </>;
  }
  return <>
    <td className="sa-num" {...label(0)}>{money(row.sales)}</td>
    {hasComparison && <>
      <td className="sa-num" {...label(1)}>{money(row.previous_sales)}</td>
      <td className={`sa-num sa-delta sa-delta--${tone(row.change_amount)}`} {...label(2)}>{signedMoney(row.change_amount)}
        <small>{row.change_percent == null ? "new" : signedPct(row.change_percent)}</small></td>
    </>}
    <td className="sa-num" {...label(hasComparison ? 3 : 1)}>{pct(row.share_percent)}</td>
  </>;
}

export default function CustomerList({ report, asOf, territoryId, filters = EMPTY, scope, list = "attention", onListChange,
  openKey, onOpen, renderPanel }) {
  const [query, setQuery] = useState("");
  const [hideFollowed, setHideFollowed] = useState(false);
  const [visible, setVisible] = useState(PAGE);
  const [attempt, setAttempt] = useState(0);
  const [printJob, setPrintJob] = useState(0);

  const healthParams = useMemo(() => ({ as_of: asOf, territory_id: territoryId, filters: customerLevelFilters(filters) }),
    [asOf, territoryId, filters]);
  const health = useSideReport(getCustomerHealth, healthParams, Boolean(asOf), attempt);
  const customers = report?.customers;
  const hasComparison = Boolean(report?.comparison);
  const comparisonDates = hasComparison ? shortRange(report.comparison.start_date, report.comparison.end_date) : "";
  const lists = CUSTOMER_LISTS.filter((item) => !item.comparison || hasComparison);
  const current = lists.find((item) => item.key === list) || lists[0];
  const summary = health.data?.summary;
  const byStatus = summary?.by_status || {};
  const allRows = useMemo(() => (report?.breakdowns?.customer || []).filter((row) => row.key !== "adjustment"), [report]);

  const counts = {
    attention: summary?.flagged, late: byStatus.late, switching: byStatus.switching,
    less: summary ? (byStatus.spending_less || 0) + (byStatus.ordering_less || 0) + (byStatus.smaller_orders || 0) : undefined,
    lost: customers?.lost?.total, new: customers?.new?.total, back: customers?.reactivated?.total, all: allRows.length,
  };

  useEffect(() => { setVisible(PAGE); }, [list, query, hideFollowed]);

  const rows = useMemo(() => {
    let source;
    if (current.source === "health") {
      source = (health.data?.rows || []).filter((row) => !current.statuses || current.statuses.includes(row.status))
        .filter((row) => !hideFollowed || !row.follow_up?.open_tasks);
    } else if (current.source === "all") source = allRows;
    else source = customers?.[current.source]?.rows || [];
    const term = query.trim().toLocaleLowerCase();
    if (!term) return source;
    return source.filter((row) => `${row.name || ""} ${row.salesman?.name || ""}`.toLocaleLowerCase().includes(term));
  }, [current, health.data, hideFollowed, allRows, customers, query]);

  const isHealth = current.source === "health";
  const columns = columnsFor(current.source, { hasComparison, comparisonDates });
  const retained = customers?.counts?.retained;
  const previous = customers?.counts?.previous;
  const totalForList = current.source === "health" ? (current.key === "attention" ? health.data?.total : counts[current.key])
    : current.source === "all" ? allRows.length : customers?.[current.source]?.total;

  return <SplitView panel={openKey && renderPanel ? renderPanel : null}>
    <section className="sa-list" aria-label="Customers" aria-busy={health.loading}>
      <div className="sa-cust-top">
        {summary ? <>
          <div className="sa-cust-risk-total"><strong>{money(summary.at_risk_weekly)}</strong><span>a week at risk</span></div>
          <p>
            {count(summary.flagged)} of {count(summary.regular_customers)} regular customers are behind their own usual pattern, as of {shortDate(asOf, { year: true })}.
            {hasComparison && previous ? ` Kept ${count(retained)} of ${count(previous)} customers from ${comparisonDates} (${pct((Number(retained) / Number(previous)) * 100, 0)}).` : ""}
          </p>
        </> : <p>{health.failed ? "The needs-attention check could not be loaded." : "Checking each customer against their usual pattern…"}
          {health.failed && <button type="button" className="sa-link-button" onClick={() => setAttempt(attempt + 1)}>Try again</button>}</p>}
      </div>

      <div className="sa-chips" role="group" aria-label="Customer lists">
        {lists.map((item) => <button type="button" key={item.key} aria-pressed={item.key === current.key}
          className={item.key === current.key ? "is-selected" : ""} onClick={() => onListChange?.(item.key)}>
          {item.label}<b>{counts[item.key] ?? "…"}</b>
        </button>)}
      </div>

      <div className="sa-tools">
        <input type="search" aria-label="Search customers" placeholder={isHealth ? "Search customers or salesmen" : "Search customers"}
          value={query} onChange={(event) => setQuery(event.target.value)} />
        {isHealth && <label className="sa-check"><input type="checkbox" checked={hideFollowed} onChange={(event) => setHideFollowed(event.target.checked)} /> Hide those with an open task</label>}
        {isHealth && rows.length > 0 && <button type="button" className="sa-outline-button" onClick={() => setPrintJob(printJob + 1)}>Print call sheet</button>}
      </div>
      {isHealth && hasItemLevelFilter(filters.map((key) => ({ dimension: String(key).split(":")[0] })))
        && <p className="sa-note">Route, vendor, category and product focus don’t apply to this list; it looks at each customer’s whole order.</p>}

      {rows.length > 0 ? <div className="sa-table-scroll"><table className="sa-list-table" aria-label={current.label}>
        <thead><tr>{columns.map((label, index) => <th scope="col" key={label} className={index ? (index === 1 && isHealth ? "" : "sa-num") : "sa-col-name"}>{label}</th>)}</tr></thead>
        <tbody>{rows.slice(0, visible).map((row) => <tr key={row.key} className={openKey === row.key ? "is-open" : ""} onClick={() => onOpen?.(row.key)}>
          <Name row={row} openKey={openKey} onOpen={(key) => onOpen?.(key)}
            sub={isHealth ? `${row.salesman?.name || "Office / Unassigned"} · usually ${cadence(row.typical_gap_days)}` : null} />
          <Cells source={current.source} row={row} hasComparison={hasComparison} labels={columns} />
        </tr>)}</tbody>
      </table></div> : <p className="sa-list-empty">{health.loading && isHealth ? "Checking customers…"
        : query ? "No customers match this search."
          : isHealth ? "No regular customers in this list are behind their usual pattern."
            : `No customers in this list for these dates.`}</p>}

      <div className="sa-list-foot">
        <span>{rows.length > visible ? `Showing ${visible} of ${rows.length}.` : rows.length ? `${count(rows.length)} ${plural(rows.length, "customer")}.` : ""}
          {totalForList > rows.length && !query && !hideFollowed ? ` The ${count(rows.length)} with the most at stake of ${count(totalForList)}.` : ""}</span>
        {rows.length > visible && <button type="button" className="sa-link-button" onClick={() => setVisible(visible + PAGE)}>Show {Math.min(PAGE, rows.length - visible)} more</button>}
      </div>
    </section>
    {printJob > 0 && isHealth && <CallSheet key={printJob} title={current.label} asOf={asOf} scope={scope} rows={rows} />}
  </SplitView>;
}
